import type { FieldDefinition } from "@estelle/domain";

/**
 * The fields the Users section offers to the filter builder, the column
 * chooser and the sort control.
 *
 * Order matters — the column chooser takes the first six as the default
 * set — so who they are, whether they can sign in, and what they hold come
 * before when they last did anything.
 *
 * `roleIds` and `groupIds` are declared filterable because a saved view
 * like "everyone in Warehouse" is an obviously useful thing to want, but the
 * fixture adapter's own filter projection for `listUsers` — `{ name, email,
 * status }` in `adapters/fixture/index.ts` — does not expose either key
 * yet. A condition on one of these compares against `undefined` and matches
 * nothing rather than throwing, the same gap `ORDER_FIELDS` documents for
 * `sku` and `warehouse`. They stay in the vocabulary, inert, rather than
 * silently missing from it.
 */
export const USER_FIELDS: FieldDefinition[] = [
  { name: "name", label: "Name", type: "text", sortable: true },
  { name: "email", label: "Email", type: "text", sortable: true },
  {
    name: "status",
    label: "Status",
    type: "enum",
    sortable: true,
    options: [
      { value: "active", label: "Active" },
      { value: "invited", label: "Invited" },
      { value: "suspended", label: "Suspended" },
    ],
  },
  /**
   * Ids, not names — same reason `CONTACT_FIELDS.typeIds` holds ids: a role
   * or group can be renamed at any time, and a filter or a saved view has to
   * keep meaning "the Warehouse role" through that rename, not "whatever was
   * called Warehouse when this view was saved." `UsersList` resolves ids to
   * names for the column itself, via `renderOverride`.
   */
  { name: "roleIds", label: "Roles", type: "tag" },
  { name: "groupIds", label: "Groups", type: "tag" },
  {
    name: "lastLoginAt",
    label: "Last sign-in",
    type: "datetime",
    sortable: true,
  },
  { name: "createdAt", label: "Added", type: "date", sortable: true },
];

/**
 * The fields the Groups section offers.
 *
 * `roleIds` carries the same filter-projection gap as `USER_FIELDS.roleIds`
 * above — the adapter's `listGroups` projects only `{ name }` — and for the
 * same reason. `memberCount` is computed by `GroupsList` from the user list
 * (`Group` itself carries no member list, only `roleIds`), so it can be
 * shown but never filtered or sorted server-side — the inverse of
 * `ORDER_FIELDS.hasInvoice`, which is filterable but not displayable; see
 * the comment on `FieldDefinition.filterable` for why that split exists.
 */
export const GROUP_FIELDS: FieldDefinition[] = [
  { name: "name", label: "Name", type: "text", sortable: true },
  { name: "description", label: "Description", type: "text" },
  { name: "roleIds", label: "Grants", type: "tag" },
  {
    name: "memberCount",
    label: "Members",
    type: "number",
    filterable: false,
  },
];

/**
 * `AuditEntry.action` values, seeded in `fixtures/access.ts`.
 *
 * Not a type in `@estelle/domain` — `action` is declared `string` there, on
 * the same reasoning as `PermissionKey`: the action vocabulary is data that
 * grows as more of the application starts writing audit entries (M15-6),
 * not a union this package would have to be edited to extend. This list
 * mirrors what is seeded today so the column reads as a sentence instead of
 * a dotted key; a value seeded later and missing from here still renders,
 * just humanised instead of labelled (see `renderCell`'s `enum` case).
 */
export const AUDIT_ACTIONS: { value: string; label: string }[] = [
  // Plain sentences, said the way a person would say what they did (#642):
  // "Uploaded a doc attachment", never "Docs.attachment.uploaded".
  { value: "auth.signin.succeeded", label: "Signed in" },
  { value: "auth.signin.failed", label: "Failed to sign in" },
  { value: "order.tags.updated", label: "Changed an order's tags" },
  { value: "order.note.updated", label: "Changed an order's note" },
  { value: "order.cancelled", label: "Cancelled an order" },
  { value: "order.stage_override.set", label: "Set an order's stage by hand" },
  {
    value: "order.stage_override.cleared",
    label: "Put an order's stage back to automatic",
  },
  { value: "orders.packing_slips.printed", label: "Printed packing slips" },
  { value: "return.received", label: "Received a return" },
  { value: "return.added_manually", label: "Added a return by hand" },
  {
    value: "return.follow_up_created",
    label: "Created a follow-up for a return",
  },
  { value: "returns.settings.updated", label: "Changed the returns settings" },
  { value: "customer.note.updated", label: "Changed a customer's note" },
  { value: "customer.tag.added", label: "Tagged a customer" },
  { value: "customer.tag.removed", label: "Removed a customer's tag" },
  { value: "comment.deleted", label: "Deleted a comment" },
  { value: "stock.adjusted", label: "Adjusted stock" },
  { value: "inventory.available.adjusted", label: "Adjusted available stock" },
  { value: "stock_transfer.created", label: "Created a stock transfer" },
  { value: "stock_transfer.dispatched", label: "Dispatched a stock transfer" },
  { value: "product.updated", label: "Changed a product" },
  { value: "variant.hsn_updated", label: "Changed a variant's HSN code" },
  { value: "invoice.cancelled", label: "Cancelled an invoice" },
  { value: "role.created", label: "Created a role" },
  { value: "role.updated", label: "Changed a role" },
  { value: "role.renamed", label: "Renamed a role" },
  { value: "role.permissions.updated", label: "Changed a role's permissions" },
  { value: "role.deleted", label: "Deleted a role" },
  { value: "group.created", label: "Created a group" },
  { value: "group.renamed", label: "Renamed a group" },
  { value: "group.members.updated", label: "Changed a group's members" },
  { value: "group.roles.updated", label: "Changed a group's roles" },
  { value: "group.deleted", label: "Deleted a group" },
  { value: "group.membership.changed", label: "Changed a group's members" },
  { value: "user.invited", label: "Invited a user" },
  { value: "user.invite.resent", label: "Resent an invite" },
  { value: "user.suspended", label: "Suspended a user" },
  { value: "user.reactivated", label: "Reactivated a user" },
  { value: "user.roles.updated", label: "Changed a user's roles" },
  { value: "tag.renamed", label: "Renamed a tag" },
  { value: "tag.merged", label: "Merged tags" },
  { value: "tag.deleted", label: "Deleted a tag" },
  { value: "order_stage.rule.created", label: "Added an order stage rule" },
  { value: "order_stage.rule.updated", label: "Changed an order stage rule" },
  { value: "order_stage.rule.deleted", label: "Deleted an order stage rule" },
  {
    value: "order_stage.settings.updated",
    label: "Changed the order stage settings",
  },
  { value: "docs.page.moved", label: "Moved a doc page" },
  { value: "docs.page.deleted", label: "Deleted a doc page" },
  { value: "docs.page.restored", label: "Restored a doc page" },
  { value: "docs.grant.set", label: "Shared a doc folder" },
  { value: "docs.grant.removed", label: "Removed a doc folder share" },
  { value: "docs.attachment.uploaded", label: "Uploaded a doc attachment" },
  { value: "docs.attachment.deleted", label: "Deleted a doc attachment" },
  { value: "view.deleted", label: "Deleted a saved view" },
  { value: "settings.company.updated", label: "Changed the company settings" },
  {
    value: "tracking.settings.updated",
    label: "Changed the tracking settings",
  },
  {
    value: "notifications.schedule.updated",
    label: "Changed the notification schedule",
  },
  { value: "dashboard.limits.updated", label: "Changed the dashboard limits" },
];

const AUDIT_ACTION_LABELS = new Map(
  AUDIT_ACTIONS.map((action) => [action.value, action.label]),
);

/**
 * An audit action as a sentence (#642). Known keys read as written above; one
 * added to the API later reads as its own words, verb first — "uploaded" in
 * `docs.attachment.uploaded` becomes "Uploaded docs attachment" — rather than
 * the dotted key itself.
 */
export function auditActionLabel(action: string): string {
  const known = AUDIT_ACTION_LABELS.get(action);
  if (known) return known;
  const parts = action.split(".").map((part) => part.replaceAll("_", " "));
  const verb = parts.pop() ?? action;
  const sentence = [verb, ...parts].join(" ").trim();
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/**
 * The fields the Audit Log section offers.
 *
 * `occurredAt`, `actorUserId`, `action` and `entityType` are all filterable
 * and sortable against the real thing: the adapter's `listAudit` projection
 * carries exactly these four keys, and defaults the sort to `occurredAt`
 * descending (`adapters/fixture/index.ts`), which is why the newest entry
 * always opens the list without anyone touching the sort control.
 *
 * `ipAddress` is declared `filterable: false` rather than left to the usual
 * "matches nothing" gap: the adapter's projection omits it entirely, so
 * `is_empty` would read every row — including the ones a sign-in actually
 * recorded an IP for — as empty. That is not an inert filter someone can
 * shrug off, it is a wrong answer to exactly the question this column
 * exists to let someone ask, so it is disabled instead of offered.
 */
export const AUDIT_FIELDS: FieldDefinition[] = [
  { name: "occurredAt", label: "When", type: "datetime", sortable: true },
  { name: "actorUserId", label: "Who", type: "reference", sortable: true },
  {
    name: "action",
    label: "Action",
    type: "enum",
    sortable: true,
    options: AUDIT_ACTIONS,
  },
  { name: "entityType", label: "Entity", type: "text", sortable: true },
  // In the chooser and the entry's own page, out of the default view (#642): a
  // raw id and an address say nothing to someone reading what happened.
  {
    name: "entityId",
    label: "Entity id",
    type: "text",
    filterable: false,
    defaultHidden: true,
  },
  {
    name: "ipAddress",
    label: "IP address",
    type: "text",
    filterable: false,
    defaultHidden: true,
  },
];

/**
 * `ChannelWrite.mutation` values, seeded in `fixtures/channel-writes.ts`.
 *
 * Same reasoning as `AUDIT_ACTIONS` above: not a domain enum because the
 * mutation vocabulary grows as more of the ERP starts writing to the channel
 * (M15's write tickets), not a union `packages/domain` should be edited to
 * extend every time. A mutation seeded later and missing from here still
 * renders — humanised instead of labelled, per `renderCell`'s `enum` case.
 */
const CHANNEL_WRITE_MUTATIONS: { value: string; label: string }[] = [
  { value: "tagsAdd", label: "Tag added" },
  { value: "tagsRemove", label: "Tag removed" },
  { value: "orderUpdate", label: "Order updated" },
  { value: "orderCancel", label: "Order cancelled" },
  { value: "inventoryAdjustQuantities", label: "Inventory adjusted" },
  { value: "productVariantsBulkUpdate", label: "Variant updated" },
  { value: "productUpdate", label: "Product updated" },
];

/** `ChannelWrite.entityType` values seeded today. See `CHANNEL_WRITE_MUTATIONS`. */
const CHANNEL_WRITE_ENTITY_TYPES: { value: string; label: string }[] = [
  { value: "order", label: "Order" },
  { value: "variant", label: "Variant" },
  { value: "inventory_level", label: "Inventory level" },
  { value: "product", label: "Product" },
];

/**
 * The fields the channel write log (#270's item 3) renders columns from.
 *
 * `status` leads rather than following identity columns the way `AUDIT_FIELDS`
 * puts `occurredAt` first: CLAUDE.md's "expected failures are rows, not logs"
 * is the entire reason this screen exists, and `error` sits last so a read
 * left to right lands on "what was attempted, against what, by whom, when" —
 * and only then, for a refusal, the channel's own words for why.
 *
 * Not wired into `sync`'s `SectionUi.fields` in `index.ts`. That section also
 * renders the placeholder sync-status board above this log, and the shared
 * toolbar's filter, columns and saved views have no way to leave a status
 * board alone while acting on the log beside it. `ShopifySync` uses this
 * vocabulary directly against the same `DataTable` every other list uses —
 * it just owns the filter and sort state itself instead of taking it from
 * the URL-driven toolbar `index.ts`'s `fields: []` disables for this section.
 */
export const CHANNEL_WRITE_FIELDS: FieldDefinition[] = [
  {
    name: "status",
    label: "Status",
    type: "enum",
    sortable: true,
    options: [
      { value: "failed", label: "Failed" },
      { value: "pending", label: "Pending" },
      { value: "succeeded", label: "Succeeded" },
    ],
  },
  {
    name: "mutation",
    label: "Mutation",
    type: "enum",
    sortable: true,
    options: CHANNEL_WRITE_MUTATIONS,
  },
  {
    name: "entityType",
    label: "Entity",
    type: "enum",
    sortable: true,
    options: CHANNEL_WRITE_ENTITY_TYPES,
  },
  { name: "entityId", label: "Entity id", type: "text" },
  {
    name: "actorUserId",
    label: "Attempted by",
    type: "reference",
    sortable: true,
  },
  { name: "createdAt", label: "When", type: "datetime", sortable: true },
  /**
   * Never truncated by `DataTable` — it has no `truncate` class on its cells
   * — because the channel's refusal text is the one thing on this row a
   * paraphrase or an ellipsis would defeat (see `ChannelWrite.error`'s doc
   * comment in `packages/domain`).
   */
  { name: "error", label: "Channel error", type: "text" },
];
